<template>
  <div class="dynamic-range-report-detail-container">
    <!-- 1. 顶部图像预览 -->
    <div class="report-header-images pdf-atomic-block">
      <div class="labeled-image-container">
        <div class="image-label-wrapper">
          <el-tag type="primary" effect="dark">{{
            $t("report.originalImage")
          }}</el-tag>
        </div>
        <div class="image-wrapper">
          <div v-if="convertingMap.original" class="image-loading">
            <i class="el-icon-loading"></i>
          </div>
          <div v-else-if="conversionErrors.original" class="image-error">
            {{ $t("error.operationFailed") }}
          </div>
          <img
            v-else
            :src="getDisplayUrl('original', reportData && reportData.image_url)"
            crossorigin="anonymous"
            alt="Original Image Preview"
          />
        </div>
      </div>
      <div class="labeled-image-container">
        <div class="image-label-wrapper">
          <el-tag type="primary" effect="dark">{{
            $t("report.roiImage")
          }}</el-tag>
        </div>
        <div class="image-wrapper">
          <div v-if="convertingMap.roi" class="image-loading">
            <i class="el-icon-loading"></i>
          </div>
          <div v-else-if="conversionErrors.roi" class="image-error">
            {{ $t("error.operationFailed") }}
          </div>
          <img
            v-else
            :src="getDisplayUrl('roi', reportData && reportData.image_roi_url)"
            crossorigin="anonymous"
            alt="ROI Image"
          />
        </div>
      </div>
    </div>

    <div class="pdf-atomic-block">
      <!-- 2. 分析结果 (Analysis result) -->
      <h4 id="analysis-result" class="section-title">
        {{ $t("report.analysis_result_title") }}
      </h4>
      <div class="side-by-side-tables">
        <!-- Left Table -->
        <el-table
          :data="analysisResultLeft"
          style="width: 100%"
          border
          class="custom-table"
        >
          <el-table-column prop="item" label="Item" align="center" />
          <el-table-column prop="value" label="Value" align="center" />
        </el-table>
        <!-- Right Table -->
        <el-table
          :data="analysisResultRight"
          style="width: 100%"
          border
          class="custom-table"
        >
          <el-table-column prop="item" label="Item" align="center" />
          <el-table-column prop="value" label="Value" align="center" />
        </el-table>
      </div>
    </div>

    <!-- 3. OECF Curve -->
    <div class="pdf-atomic-block">
      <h4 id="oecf-curve" class="section-title">
        {{ $t("report.OECF_curve") }}
      </h4>
      <div class="chart-container">
        <div class="chart-inner-wrapper">
          <v-chart
            class="echart"
            :option="oecfCurveOption"
            ref="oecfCurveOption_Chart"
            autoresize
          />
        </div>
      </div>
      <div
        v-if="reportData.result.oecf_curve.result_text"
        class="result-summary-box"
      >
        <strong>{{ $t("report.result") }}</strong>
        {{ reportData.result.oecf_curve.result_text }}
      </div>
    </div>

    <div class="pdf-atomic-block">
      <el-table
        :data="reportData.result.oecf_curve.form"
        style="width: 100%"
        border
        class="custom-table"
      >
        <el-table-column label="Item" width="80" align="center">
          <template #default="scope">
            {{ scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column
          prop="density"
          label="Density"
          align="center"
          show-overflow-tooltip
        />
        <el-table-column
          prop="pixel"
          label="Pixel"
          align="center"
          show-overflow-tooltip
        />
        <el-table-column
          prop="log_pixel_pix_sat"
          label="Log(pixel/pix_sat)"
          align="center"
          show-overflow-tooltip
        />
      </el-table>
    </div>

    <!-- Noise Data 模块 -->
    <div class="pdf-atomic-block">
      <h5 id="noise-data" class="sub-section-title">
        {{ $t("report.Noise_data") }}
      </h5>
      <div class="chart-container">
        <div class="chart-inner-wrapper">
          <v-chart
            class="echart"
            :option="noiseValueOption"
            ref="noiseValueOption_Chart"
            autoresize
          />
        </div>
      </div>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">noise(%normalized to max pixel value)</h5>
      <el-table
        :data="reportData.result.noise_data.form"
        style="width: 100%"
        border
        class="custom-table"
      >
        <el-table-column label="Item" width="80" align="center">
          <template #default="scope">
            {{ scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column
          prop="r"
          label="R"
          align="center"
          show-overflow-tooltip
        />
        <el-table-column
          prop="g"
          label="G"
          align="center"
          show-overflow-tooltip
        />
        <el-table-column
          prop="b"
          label="B"
          align="center"
          show-overflow-tooltip
        />
        <el-table-column
          prop="y"
          label="Y"
          align="center"
          show-overflow-tooltip
        />
      </el-table>
    </div>

    <!-- 4. SNR -->
    <div class="pdf-atomic-block">
      <h4 id="snr" class="section-title">SNR</h4>
      <h5 class="sub-section-title">RMS noise SNR chart</h5>
      <div class="chart-container">
        <div class="chart-inner-wrapper">
          <v-chart
            class="echart"
            :option="rmsNoiseSnrOption"
            ref="rmsNoiseSnrOption_Chart"
            autoresize
          />
        </div>
      </div>
      <div
        v-if="reportData.result.rms_noise_snr.result_text"
        class="result-summary-box"
      >
        <strong>{{ $t("report.result") }}</strong>
        {{ reportData.result.rms_noise_snr.result_text }}
      </div>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">RMS noise SNR</h5>
      <el-table
        :data="reportData.result.rms_noise_snr.form"
        style="width: 100%"
        border
        class="custom-table"
      >
        <el-table-column label="Item" width="80" align="center">
          <template #default="scope">
            {{ scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column
          prop="r"
          label="R"
          align="center"
          show-overflow-tooltip
        />
        <el-table-column
          prop="g"
          label="G"
          align="center"
          show-overflow-tooltip
        />
        <el-table-column
          prop="b"
          label="B"
          align="center"
          show-overflow-tooltip
        />
        <el-table-column
          prop="y"
          label="Y"
          align="center"
          show-overflow-tooltip
        />
      </el-table>
    </div>

    <!-- f-stop noise SNR chart 模块 -->
    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">f-stop noise SNR chart</h5>
      <div class="chart-container">
        <div class="chart-inner-wrapper">
          <v-chart
            class="echart"
            :option="fStopNoiseSnrOption"
            ref="fStopNoiseSnrOption_Chart"
            autoresize
          />
        </div>
      </div>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">f-stop noise SNR</h5>
      <el-table
        :data="reportData.result.f_stop_noise_snr.form"
        style="width: 100%"
        border
        class="custom-table"
      >
        <el-table-column label="Item" width="80" align="center">
          <template #default="scope">
            {{ scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column
          prop="r"
          label="R"
          align="center"
          show-overflow-tooltip
        />
        <el-table-column
          prop="g"
          label="G"
          align="center"
          show-overflow-tooltip
        />
        <el-table-column
          prop="b"
          label="B"
          align="center"
          show-overflow-tooltip
        />
        <el-table-column
          prop="y"
          label="Y"
          align="center"
          show-overflow-tooltip
        />
      </el-table>
    </div>

    <!-- 5. SNR ISO15739 -->
    <div class="pdf-atomic-block">
      <h4 id="snr-iso15739" class="section-title">SNR ISO15739</h4>
      <h5 class="sub-section-title">SNR ISO15739</h5>
      <div class="chart-container">
        <div class="chart-inner-wrapper">
          <v-chart
            class="echart"
            :option="snrIsoOption"
            ref="snrIsoOption_Chart"
            autoresize
          />
        </div>
      </div>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">SNR</h5>
      <el-table
        :data="reportData.result.snr_iso15739.form"
        style="max-width: 600px; margin: 20px auto"
        border
        class="custom-table"
      >
        <el-table-column prop="item" label="Item"></el-table-column>
        <el-table-column prop="snr" label="SNR"></el-table-column>
      </el-table>
    </div>

    <!-- 6. Visual noise -->
    <div class="pdf-atomic-block">
      <h4 id="visual-noise" class="section-title">
        {{ $t("report.Visual_noise") }}
      </h4>
      <h5 class="sub-section-title">{{ $t("report.visual_noise1") }}</h5>
      <div class="chart-container">
        <div class="chart-inner-wrapper">
          <v-chart
            class="echart"
            :option="visualNoise1Option"
            ref="visualNoise1Option_Chart"
            autoresize
          />
        </div>
      </div>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">
        {{ $t("report.Observation_condition") }}
      </h5>
      <el-table
        :data="[{ distance: 500, height: 375 }]"
        style="max-width: 600px; margin: 20px auto"
        border
        class="custom-table"
      >
        <el-table-column
          prop="distance"
          :label="$t('report.Observation_distance')"
          align="center"
        />
        <el-table-column
          prop="height"
          :label="$t('report.Image_height')"
          align="center"
        />
      </el-table>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">
        {{ $t("report.Visual_noise_value") }}
      </h5>
      <el-table
        :data="reportData.result.visual_noise_vn1.form"
        style="width: 100%"
        border
        class="custom-table"
      >
        <el-table-column label="Item" width="80" align="center">
          <template #default="scope">
            {{ scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column prop="vn" label="VN" align="center" />
        <el-table-column prop="delta_L" label="ΔL*" align="center" />
        <el-table-column prop="delta_u" label="ΔU*" align="center" />
        <el-table-column prop="delta_v" label="ΔV*" align="center" />
      </el-table>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">{{ $t("report.visual_noise2") }}</h5>
      <div class="chart-container">
        <div class="chart-inner-wrapper">
          <v-chart
            class="echart"
            :option="visualNoise2Option"
            ref="visualNoise2Option_Chart"
            autoresize
          />
        </div>
      </div>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">
        {{ $t("report.Observation_condition") }}
      </h5>
      <el-table
        :data="[{ distance: 250, height: 55 }]"
        style="max-width: 600px; margin: 20px auto"
        border
        class="custom-table"
      >
        <el-table-column
          prop="distance"
          :label="$t('report.Observation_distance')"
          align="center"
        />
        <el-table-column
          prop="height"
          :label="$t('report.Image_height')"
          align="center"
        />
      </el-table>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">{{ $t("report.Visual_noise_value") }}</h5>
      <el-table
        :data="reportData.result.visual_noise_vn2.form"
        style="width: 100%"
        border
        class="custom-table"
      >
        <el-table-column label="Item" width="80" align="center">
          <template #default="scope">
            {{ scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column prop="vn" label="VN" align="center" />
        <el-table-column prop="delta_L" label="ΔL*" align="center" />
        <el-table-column prop="delta_u" label="ΔU*" align="center" />
        <el-table-column prop="delta_v" label="ΔV*" align="center" />
      </el-table>
    </div>

    <!-- 7. Visual SNR -->
    <div class="pdf-atomic-block">
      <h4 id="visual-snr" class="section-title">
        {{ $t("report.Visual_SNR") }}
      </h4>
      <h5 class="sub-section-title">{{ $t("report.Visual_SNR") }}1</h5>
      <div class="chart-container">
        <div class="chart-inner-wrapper">
          <v-chart
            class="echart"
            :option="visualSnr1Option"
            ref="visualSnr1Option_Chart"
            autoresize
          />
        </div>
      </div>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">
        {{ $t("report.Observation_condition") }}
      </h5>
      <el-table
        :data="[{ distance: 500, height: 375 }]"
        style="max-width: 600px; margin: 20px auto"
        border
        class="custom-table"
      >
        <el-table-column
          prop="distance"
          :label="$t('report.Observation_distance')"
          align="center"
        />
        <el-table-column
          prop="height"
          :label="$t('report.Image_height')"
          align="center"
        />
      </el-table>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">{{ $t("report.Visual_SNR1_value") }}</h5>
      <el-table
        :data="reportData.result.visual_snr1.form"
        style="width: 100%"
        border
        class="custom-table"
      >
        <el-table-column label="Item" width="80" align="center">
          <template #default="scope">
            {{ scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column prop="vsnr" label="VSNR" align="center" />
        <el-table-column prop="snrL" label="snrL*" align="center" />
        <el-table-column prop="snrU" label="snrU*" align="center" />
        <el-table-column prop="snrV" label="snrV*" align="center" />
      </el-table>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">{{ $t("report.Visual_SNR") }}2</h5>
      <div class="chart-container">
        <div class="chart-inner-wrapper">
          <v-chart
            class="echart"
            :option="visualSnr2Option"
            ref="visualSnr2Option_Chart"
            autoresize
          />
        </div>
      </div>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">
        {{ $t("report.Observation_condition") }}
      </h5>
      <el-table
        :data="[{ distance: 250, height: 55 }]"
        style="max-width: 600px; margin: 20px auto"
        border
        class="custom-table"
      >
        <el-table-column
          prop="distance"
          :label="$t('report.Observation_distance')"
          align="center"
        />
        <el-table-column
          prop="height"
          :label="$t('report.Image_height')"
          align="center"
        />
      </el-table>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">{{ $t("report.Visual_SNR2_value") }}</h5>
      <el-table
        :data="reportData.result.visual_snr2.form"
        style="width: 100%"
        border
        class="custom-table"
      >
        <el-table-column label="Item" width="80" align="center">
          <template #default="scope">
            {{ scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column prop="vsnr" label="VSNR" align="center" />
        <el-table-column prop="snrL" label="snrL*" align="center" />
        <el-table-column prop="snrU" label="snrU*" align="center" />
        <el-table-column prop="snrV" label="snrV*" align="center" />
      </el-table>
    </div>

    <!-- ** 回到顶部按钮 ** -->
    <el-backtop
      v-if="!isPrinting"
      :target="backtopTarget"
      :right="40"
      :bottom="40"
    />
  </div>
</template>

<script>
import { backtopMixin } from "@/mixins/backtopMixin";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
} from "echarts/components";
import VChart from "vue-echarts";
import UTIF from "utif";

use([
  CanvasRenderer,
  LineChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
]);

export default {
  name: "DynamicRangeReportDetail",
  mixins: [backtopMixin],
  components: {
    VChart,
  },
  props: {
    reportData: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      isPrinting: false,
      convertedImageUrls: {
        original: null,
        roi: null,
      },
      convertingMap: {
        original: false,
        roi: false,
      },
      conversionErrors: {},
    };
  },
  computed: {
    analysisResultLeft() {
      if (!this.reportData?.result?.analysis_result_form) return [];
      const summary = this.reportData.result.analysis_result_form;
      return [
        {
          item: this.$t("report.Average_noise_R"),
          value: summary.average_noise_r,
        },
        {
          item: this.$t("report.Average_noise_G"),
          value: summary.average_noise_g,
        },
        {
          item: this.$t("report.Average_noise_B"),
          value: summary.average_noise_b,
        },
        {
          item: this.$t("report.Average_noise_Y"),
          value: summary.average_noise_y,
        },
        { item: "DR_dB(SNR=1)", value: summary.dr_db_snr1 },
        { item: "DR_dB(SNR=2)", value: summary.dr_db_snr2 },
        { item: "DR_dB(SNR=4)", value: summary.dr_db_snr4 },
        { item: "DR_dB(SNR=10)", value: summary.dr_db_snr10 },
        { item: this.$t("report.Average_SNR"), value: summary.average_snr },
      ];
    },
    analysisResultRight() {
      if (!this.reportData?.result?.analysis_result_form) return [];
      const summary = this.reportData.result.analysis_result_form;
      return [
        { item: "Gamma", value: summary.gamma },
        { item: "Exposure Error", value: summary.exposure_error },
        {
          item: this.$t("report.8_gray_scale"),
          value: summary.delta_y_gray_scale,
        },
        {
          item: this.$t("report.ISO_dynamic_den"),
          value: summary.iso_dynamic_range_den,
        },
        {
          item: this.$t("report.ISO_dynamic_f-stop"),
          value: summary.iso_dynamic_range_f_stop,
        },
        {
          item: this.$t("report.ISO_dynamic_dB"),
          value: summary.iso_dynamic_range_db,
        },
        { item: "snr_total", value: summary.snr_total },
        { item: "snr_total(dB)", value: summary.snr_total_db },
        { item: "σ_total", value: summary.σ_total },
      ];
    },
    oecfCurveOption() {
      if (!this.reportData?.result?.oecf_curve?.graph) return {};
      const graph = this.reportData.result.oecf_curve.graph;
      return {
        title: {
          text: "OECF curve",
          left: "center",
          top: "3%",
          textStyle: {
            fontSize: 12,
            fontWeight: "normal",
          },
        },
        color: ["#FF0000", "#90EE90", "#00a0e9", "#909399"],
        tooltip: { trigger: "axis" },
        legend: {
          data: ["R", "G", "B", "Y"],
          bottom: "2%",
          left: "center",
          itemWidth: 15,
          itemHeight: 10,
          textStyle: {
            padding: [0, 15, 0, 2],
          },
          formatter: (name) => {
            return name;
          },
        },
        grid: {
          left: "8%",
          right: "5%",
          bottom: "22%",
          top: "15%",
          containLabel: false,
        },
        xAxis: {
          type: "value",
          name: "Log Exposure (-Target density)",
          nameLocation: "middle",
          nameGap: 35,
          axisLine: { show: true, onZero: false, lineStyle: { color: "#333" } },
          axisTick: { show: true, lineStyle: { color: "#333" } },
          splitLine: {
            show: true,
            lineStyle: { type: "dashed", color: "#DDD" },
          },
          min: graph.x_range ? graph.x_range[0] : null,
          max: graph.x_range ? graph.x_range[1] : null,
        },
        yAxis: {
          type: "value",
          name: "Log (Pixel level/255)",
          nameLocation: "middle",
          nameGap: 50,
          axisLine: { show: true, onZero: false, lineStyle: { color: "#333" } },
          axisTick: { show: true, lineStyle: { color: "#333" } },
          minorTick: {
            show: true,
            lineStyle: {
              color: "#666",
            },
          },
          splitLine: {
            show: true,
            lineStyle: { type: "dashed", color: "#DDD" },
          },
          min: graph.y_range ? graph.y_range[0] : null,
          max: graph.y_range ? graph.y_range[1] : null,
        },
        series: graph.series.map((s, index) => ({
          name: ["R", "G", "B", "Y"][index],
          data: s.data,
          type: "line",
          symbol: "circle",
          symbolSize: 6,
          lineStyle: {
            type: "dashed",
          },
        })),
      };
    },
    noiseValueOption() {
      if (!this.reportData?.result?.noise_data?.graph) return {};
      const graph = this.reportData.result.noise_data.graph;
      return {
        title: {
          text: "Noise value",
          left: "center",
          top: "3%",
          textStyle: {
            fontSize: 12,
            fontWeight: "normal",
          },
        },
        color: ["#FF0000", "#90EE90", "#00a0e9", "#909399"],
        tooltip: { trigger: "axis" },
        legend: {
          data: ["R", "G", "B", "Y"],
          bottom: "2%",
          left: "center",
          itemWidth: 15,
          itemHeight: 10,
          textStyle: {
            padding: [0, 15, 0, 2],
          },
        },
        grid: {
          left: "8%",
          right: "5%",
          bottom: "22%",
          top: "15%",
          containLabel: false,
        },
        xAxis: {
          type: "value",
          name: "Zone",
          nameLocation: "middle",
          nameGap: 35,
          axisLine: { show: true, onZero: false, lineStyle: { color: "#333" } },
          axisTick: { show: true, lineStyle: { color: "#333" } },
          splitLine: {
            show: true,
            lineStyle: { type: "dashed", color: "#DDD" },
          },
          min: graph.x_range ? graph.x_range[0] : null,
          max: graph.x_range ? graph.x_range[1] : null,
          interval: 5,
        },
        yAxis: {
          type: "value",
          name: "SNR (dB)(20*log10(S/N))",
          nameLocation: "middle",
          nameGap: 60,
          axisLine: { show: true, onZero: false, lineStyle: { color: "#333" } },
          axisTick: { show: true, lineStyle: { color: "#333" } },
          minorTick: { show: true },
          splitLine: {
            show: true,
            lineStyle: { type: "dashed", color: "#DDD" },
          },
          min: graph.y_range ? graph.y_range[0] : null,
          max: graph.y_range ? graph.y_range[1] : null,
        },
        series: graph.series.map((s, index) => ({
          name: ["R", "G", "B", "Y"][index],
          data: s.data,
          type: "line",
          symbol: "none",
          lineStyle: {
            type: "dashed",
          },
        })),
      };
    },
    rmsNoiseSnrOption() {
      if (!this.reportData?.result?.rms_noise_snr?.graph) return {};
      const graph = this.reportData.result.rms_noise_snr.graph;
      return {
        title: {
          text: "RMS noise SNR chart",
          left: "center",
          top: "3%",
          textStyle: {
            fontSize: 12,
            fontWeight: "normal",
          },
        },
        color: ["#FF0000", "#90EE90", "#00a0e9", "#909399"],
        tooltip: { trigger: "axis" },
        legend: {
          data: ["R", "G", "B", "Y"],
          bottom: "2%",
          left: "center",
          itemWidth: 15,
          itemHeight: 10,
          textStyle: {
            padding: [0, 15, 0, 2],
          },
        },
        grid: {
          left: "8%",
          right: "5%",
          bottom: "22%",
          top: "15%",
          containLabel: false,
        },
        xAxis: {
          type: "value",
          name: "Log Exposure (-Target density)",
          nameLocation: "middle",
          nameGap: 35,
          axisLine: { show: true, onZero: false, lineStyle: { color: "#333" } },
          axisTick: { show: true, lineStyle: { color: "#333" } },
          splitLine: {
            show: true,
            lineStyle: { type: "dashed", color: "#DDD" },
          },
          min: graph.x_range ? graph.x_range[0] : null,
          max: graph.x_range ? graph.x_range[1] : null,
        },
        yAxis: {
          type: "value",
          name: "SNR (dB)(20*log10(S/N))",
          nameLocation: "middle",
          nameGap: 60,
          axisLine: { show: true, onZero: false, lineStyle: { color: "#333" } },
          axisTick: { show: true, lineStyle: { color: "#333" } },
          minorTick: { show: true },
          splitLine: {
            show: true,
            lineStyle: { type: "dashed", color: "#DDD" },
          },
          min: graph.y_range ? graph.y_range[0] : null,
          max: graph.y_range ? graph.y_range[1] : null,
        },
        series: graph.series.map((s, index) => ({
          name: ["R", "G", "B", "Y"][index],
          data: s.data,
          type: "line",
          symbol: "none",
          lineStyle: {
            type: "dashed",
          },
        })),
      };
    },

    fStopNoiseSnrOption() {
      if (!this.reportData?.result?.f_stop_noise_snr?.graph) return {};
      const graph = this.reportData.result.f_stop_noise_snr.graph;
      const markLinesData = graph.mark_lines || [];
      const defaultColors = ["#FF0000", "#90EE90", "#00a0e9", "#909399"];

      const toSuperscript = (n) => {
        const supers = "⁰¹²³⁴⁵⁶⁷⁸⁹⁻";
        return String(n)
          .split("")
          .map((char) => supers[char] || char)
          .join("");
      };

      const finalSeries = [];
      const legendData = [];

      graph.series.forEach((s, index) => {
        const seriesName = ["R", "G", "B", "Y"][index];
        const positiveData = [];
        const negativeData = [];

        s.data.forEach((point) => {
          if (point && typeof point[1] === "number") {
            if (point[1] > 0) {
              positiveData.push(point);
            } else if (point[1] < 0) {
              negativeData.push({
                value: [point[0], -point[1]], // Y值取绝对值
                originalValue: point[1], // 保存原始负值
              });
            }
          }
        });

        if (positiveData.length > 0) {
          legendData.push(seriesName);
          finalSeries.push({
            name: seriesName,
            data: positiveData,
            type: "line",
            symbol: "circle",
            symbolSize: 4,
            lineStyle: { type: "dashed" },
            color: defaultColors[index % defaultColors.length],
          });
        }

        if (negativeData.length > 0) {
          const negativeSeriesName = `${seriesName} (Negative)`;
          legendData.push(negativeSeriesName);
          finalSeries.push({
            name: negativeSeriesName,
            data: negativeData,
            type: "line",
            symbol: "diamond",
            symbolSize: 5,
            lineStyle: { type: "dotted" },
            color: defaultColors[index % defaultColors.length],
            itemStyle: {
              color: "rgba(255, 100, 100, 0.8)",
            },
          });
        }
      });

      markLinesData.forEach((line) => {
        legendData.push(line.name);
        finalSeries.push({
          name: line.name,
          type: "line",
          data: [line.start_point, line.end_point],
          symbol: "none",
          color: "#909399",
          lineStyle: {
            type: "solid",
            width: 2,
            color: "#909399",
          },
          tooltip: { show: false },
        });
      });

      return {
        title: {
          text: "f-stop noise SNR chart",
          left: "center",
          top: "3%",
          textStyle: { fontSize: 12, fontWeight: "normal" },
        },
        tooltip: {
          trigger: "axis",
          formatter: (params) => {
            let tooltipContent = `${params[0].axisValueLabel}<br/>`;
            params.forEach((param) => {
              const value =
                param.data.originalValue !== undefined
                  ? param.data.originalValue
                  : param.value[1];
              tooltipContent += `${param.marker} ${
                param.seriesName
              }: ${value.toFixed(4)}<br/>`;
            });
            return tooltipContent;
          },
        },
        legend: { data: legendData, bottom: "2%", left: "center" },
        grid: {
          left: "8%",
          right: "5%",
          bottom: "26%",
          top: "15%",
          containLabel: true,
        },
        xAxis: {
          type: "value",
          name: "Log Exposure (-Target density)",
          nameLocation: "middle",
          nameGap: 35,
          min: graph.x_range ? graph.x_range[0] : null,
          max: graph.x_range ? graph.x_range[1] : null,
        },

        yAxis: {
          type: "log",
          name: "SNR (1/f-stop noise)",
          nameLocation: "middle",
          nameGap: 60,
          logBase: 10,
          min: 0.01, // 10⁻²
          max: graph.y_range ? graph.y_range[1] + 10000 : null, // 10⁵
          axisLine: { show: true, onZero: false },
          axisTick: { show: true },
          splitLine: { show: true, lineStyle: { type: "dashed" } },
          minorTick: { show: false },
          minorSplitLine: { show: false },

          axisLabel: {
            formatter: (value) => {
              const power = Math.log10(value);
              if (Math.abs(power - Math.round(power)) < 1e-9) {
                const roundedPower = Math.round(power);

                if (roundedPower === -2) {
                  return `-10${toSuperscript(2)}`; // 10⁻² 显示为 -10²
                }
                if (roundedPower === -1) {
                  return `-10${toSuperscript(1)}`; // 10⁻¹ 显示为 -10¹
                }

                return `10${toSuperscript(roundedPower)}`;
              }
              return "";
            },
          },
        },
        series: finalSeries,
      };
    },

    snrIsoOption() {
      if (!this.reportData?.result?.snr_iso15739?.graph) return {};
      const graph = this.reportData.result.snr_iso15739.graph;
      const dashedSeriesData = graph.series || [];
      const markLines = graph.mark_lines || [];

      const dashedSeries = dashedSeriesData.map((s, index) => ({
        name: ["SNR Value"][index],
        data: s.data,
        type: "line",
        symbol: "none",
        lineStyle: {
          type: "dashed",
          color: "#F56564",
        },
        color: "#F56564",
      }));

      const solidSeries = markLines.map((line, index) => ({
        name: line.name,
        type: "line",
        data: [line.start_point, line.end_point],
        symbol: "none",
        lineStyle: {
          type: "solid",
          width: 2,
          color: ["#D689DE"][index],
        },
        color: ["#D689DE"][index],
        tooltip: {
          show: false,
        },
      }));

      const legendData = [
        "SNR Value",
        ...markLines.filter((ml) => ml.name).map((ml) => ml.name),
      ];

      return {
        title: {
          text: "SNR ISO15739",
          left: "center",
          top: "3%",
          textStyle: { fontSize: 12, fontWeight: "normal" },
        },
        tooltip: { trigger: "axis" },
        legend: {
          data: legendData,
          bottom: "2%",
          left: "center",
          itemWidth: 15,
          itemHeight: 10,
          textStyle: { padding: [0, 15, 0, 2] },
        },
        grid: {
          left: "5%",
          right: "5%",
          bottom: "22%",
          top: "15%",
          containLabel: false,
        },
        xAxis: {
          type: "value",
          name: "Log Exposure (-Target density)",
          nameLocation: "middle",
          nameGap: 35,
          axisLine: { show: true, onZero: false, lineStyle: { color: "#333" } },
          axisTick: { show: true, lineStyle: { color: "#333" } },
          splitLine: {
            show: true,
            lineStyle: { type: "dashed", color: "#DDD" },
          },
          min: graph.x_range ? graph.x_range[0] : null,
          max: graph.x_range ? graph.x_range[1] : null,
        },
        yAxis: {
          type: "value",
          name: "SNR",
          nameLocation: "middle",
          nameGap: 40,
          axisLine: { show: true, onZero: false, lineStyle: { color: "#333" } },
          axisTick: { show: true, lineStyle: { color: "#333" } },
          minorTick: { show: true },
          splitLine: {
            show: true,
            lineStyle: { type: "dashed", color: "#DDD" },
          },
          min: graph.y_range ? graph.y_range[0] : null,
          max: graph.y_range ? graph.y_range[1] : null,
        },
        series: [...dashedSeries, ...solidSeries],
      };
    },
    visualNoise1Option() {
      if (!this.reportData?.result?.visual_noise_vn1?.graph) return {};
      const graph = this.reportData.result.visual_noise_vn1.graph;
      const seriesData = graph.series || [];

      const seriesStyles = {
        delta_V: { color: "#FF0000", lineStyle: { type: "dashed" } },
        VN: { color: "#00a0e9", lineStyle: { type: "dashed" } },
        delta_U: { color: "#90EE90", lineStyle: { type: "dashed" } },
        delta_L: { color: "#f7b500", lineStyle: { type: "dashed" } },
      };

      return {
        title: {
          text: "Visual noise(VN1)",
          left: "center",
          top: "3%",
          textStyle: {
            fontSize: 12,
            fontWeight: "normal",
          },
        },
        tooltip: { trigger: "axis" },
        legend: {
          data: ["delta_V", "VN", "delta_U", "delta_L"],
          bottom: "2%",
          left: "center",
          itemWidth: 25,
          itemHeight: 10,
          textStyle: {
            padding: [0, 15, 0, 2],
          },
        },
        grid: {
          left: "8%",
          right: "5%",
          bottom: "22%",
          top: "15%",
          containLabel: false,
        },
        xAxis: {
          type: "value",
          name: "Log Exposure (-Target density)",
          nameLocation: "middle",
          nameGap: 35,
          axisLine: { show: true, onZero: false, lineStyle: { color: "#333" } },
          axisTick: { show: true, lineStyle: { color: "#333" } },
          splitLine: {
            show: true,
            lineStyle: { type: "dashed", color: "#DDD" },
          },
          min: graph.x_range ? graph.x_range[0] : null,
          max: graph.x_range ? graph.x_range[1] : null,
        },
        yAxis: {
          type: "value",
          name: "Log (Pixel level/255)",
          nameLocation: "middle",
          nameGap: 50,
          axisLine: { show: true, onZero: false, lineStyle: { color: "#333" } },
          axisTick: { show: true, lineStyle: { color: "#333" } },
          minorTick: { show: true },
          splitLine: {
            show: true,
            lineStyle: { type: "dashed", color: "#DDD" },
          },
          min: graph.y_range ? graph.y_range[0] : null,
          max: graph.y_range ? graph.y_range[1] : null,
        },
        series: seriesData.map((s, index) => {
          const seriesName = ["delta_V", "VN", "delta_U", "delta_L"][index];
          return {
            name: seriesName,
            data: s.data,
            type: "line",
            symbol: "none",
            color: seriesStyles[seriesName]?.color,
            lineStyle: seriesStyles[seriesName]?.lineStyle,
          };
        }),
      };
    },
    visualNoise2Option() {
      if (!this.reportData?.result?.visual_noise_vn2?.graph) return {};
      const graph = this.reportData.result.visual_noise_vn2.graph;
      const seriesData = graph.series || [];

      const seriesStyles = {
        delta_V: { color: "#FF0000", lineStyle: { type: "dashed" } },
        VN: { color: "#00a0e9", lineStyle: { type: "dashed" } },
        delta_U: { color: "#90EE90", lineStyle: { type: "dashed" } },
        delta_L: { color: "#f7b500", lineStyle: { type: "dashed" } },
      };

      return {
        title: {
          text: "Visual noise(VN2)",
          left: "center",
          top: "3%",
          textStyle: {
            fontSize: 12,
            fontWeight: "normal",
          },
        },
        tooltip: { trigger: "axis" },
        legend: {
          data: ["delta_V", "VN", "delta_U", "delta_L"],
          bottom: "2%",
          left: "center",
          itemWidth: 25,
          itemHeight: 10,
          textStyle: {
            padding: [0, 15, 0, 2],
          },
        },
        grid: {
          left: "8%",
          right: "5%",
          bottom: "22%",
          top: "15%",
          containLabel: false,
        },
        xAxis: {
          type: "value",
          name: "Log Exposure (-Target density)",
          nameLocation: "middle",
          nameGap: 35,
          axisLine: { show: true, onZero: false, lineStyle: { color: "#333" } },
          axisTick: { show: true, lineStyle: { color: "#333" } },
          splitLine: {
            show: true,
            lineStyle: { type: "dashed", color: "#DDD" },
          },
          min: graph.x_range ? graph.x_range[0] : null,
          max: graph.x_range ? graph.x_range[1] : null,
        },
        yAxis: {
          type: "value",
          name: "Log (Pixel level/255)",
          nameLocation: "middle",
          nameGap: 50,
          axisLine: { show: true, onZero: false, lineStyle: { color: "#333" } },
          axisTick: { show: true, lineStyle: { color: "#333" } },
          minorTick: { show: true },
          splitLine: {
            show: true,
            lineStyle: { type: "dashed", color: "#DDD" },
          },
          min: graph.y_range ? graph.y_range[0] : null,
          max: graph.y_range ? graph.y_range[1] : null,
        },
        series: seriesData.map((s, index) => {
          const seriesName = ["delta_V", "VN", "delta_U", "delta_L"][index];
          return {
            name: seriesName,
            data: s.data,
            type: "line",
            symbol: "none",
            color: seriesStyles[seriesName]?.color,
            lineStyle: seriesStyles[seriesName]?.lineStyle,
          };
        }),
      };
    },
    visualSnr1Option() {
      if (!this.reportData?.result?.visual_snr1?.graph) return {};
      const graph = this.reportData.result.visual_snr1.graph;
      const seriesData = graph.series || [];

      const seriesStyles = {
        delta_V: { color: "#FF0000", lineStyle: { type: "dashed" } },
        VN: { color: "#00a0e9", lineStyle: { type: "dashed" } },
        delta_U: { color: "#90EE90", lineStyle: { type: "dashed" } },
        delta_L: { color: "#f7b500", lineStyle: { type: "dashed" } },
      };

      return {
        title: {
          text: "Visual SNR1",
          left: "center",
          top: "3%",
          textStyle: {
            fontSize: 12,
            fontWeight: "normal",
          },
        },
        tooltip: { trigger: "axis" },
        legend: {
          data: ["delta_V", "VN", "delta_U", "delta_L"],
          bottom: "2%",
          left: "center",
          itemWidth: 25,
          itemHeight: 10,
          textStyle: {
            padding: [0, 15, 0, 2],
          },
        },
        grid: {
          left: "8%",
          right: "5%",
          bottom: "22%",
          top: "15%",
          containLabel: false,
        },
        xAxis: {
          type: "value",
          name: "Log Exposure (-Target density)",
          nameLocation: "middle",
          nameGap: 35,
          axisLine: { show: true, onZero: false, lineStyle: { color: "#333" } },
          axisTick: { show: true, lineStyle: { color: "#333" } },
          splitLine: {
            show: true,
            lineStyle: { type: "dashed", color: "#DDD" },
          },
          min: graph.x_range ? graph.x_range[0] : null,
          max: graph.x_range ? graph.x_range[1] : null,
        },
        yAxis: {
          type: "value",
          name: "Log (Pixel level/255)",
          nameLocation: "middle",
          nameGap: 50,
          axisLine: { show: true, onZero: false, lineStyle: { color: "#333" } },
          axisTick: { show: true, lineStyle: { color: "#333" } },
          minorTick: { show: true },
          splitLine: {
            show: true,
            lineStyle: { type: "dashed", color: "#DDD" },
          },
          min: graph.y_range ? graph.y_range[0] : null,
          max: graph.y_range ? graph.y_range[1] : null,
        },
        series: seriesData.map((s, index) => {
          const seriesName = ["delta_V", "VN", "delta_U", "delta_L"][index];
          return {
            name: seriesName,
            data: s.data,
            type: "line",
            symbol: "none",
            color: seriesStyles[seriesName]?.color,
            lineStyle: seriesStyles[seriesName]?.lineStyle,
          };
        }),
      };
    },
    visualSnr2Option() {
      if (!this.reportData?.result?.visual_snr2?.graph) return {};
      const graph = this.reportData.result.visual_snr2.graph;
      const seriesData = graph.series || [];

      const seriesStyles = {
        delta_V: { color: "#FF0000", lineStyle: { type: "dashed" } },
        VN: { color: "#00a0e9", lineStyle: { type: "dashed" } },
        delta_U: { color: "#90EE90", lineStyle: { type: "dashed" } },
        delta_L: { color: "#f7b500", lineStyle: { type: "dashed" } },
      };

      return {
        title: {
          text: "Visual SNR2",
          left: "center",
          top: "3%",
          textStyle: {
            fontSize: 12,
            fontWeight: "normal",
          },
        },
        tooltip: { trigger: "axis" },
        legend: {
          data: ["delta_V", "VN", "delta_U", "delta_L"],
          bottom: "2%",
          left: "center",
          itemWidth: 25,
          itemHeight: 10,
          textStyle: {
            padding: [0, 15, 0, 2],
          },
        },
        grid: {
          left: "8%",
          right: "5%",
          bottom: "22%",
          top: "15%",
          containLabel: false,
        },
        xAxis: {
          type: "value",
          name: "Log Exposure (-Target density)",
          nameLocation: "middle",
          nameGap: 35,
          axisLine: { show: true, onZero: false, lineStyle: { color: "#333" } },
          axisTick: { show: true, lineStyle: { color: "#333" } },
          splitLine: {
            show: true,
            lineStyle: { type: "dashed", color: "#DDD" },
          },
          min: graph.x_range ? graph.x_range[0] : null,
          max: graph.x_range ? graph.x_range[1] : null,
        },
        yAxis: {
          type: "value",
          name: "Log (Pixel level/255)",
          nameLocation: "middle",
          nameGap: 50,
          axisLine: { show: true, onZero: false, lineStyle: { color: "#333" } },
          axisTick: { show: true, lineStyle: { color: "#333" } },
          minorTick: { show: true },
          splitLine: {
            show: true,
            lineStyle: { type: "dashed", color: "#DDD" },
          },
          min: graph.y_range ? graph.y_range[0] : null,
          max: graph.y_range ? graph.y_range[1] : null,
        },
        series: seriesData.map((s, index) => {
          const seriesName = ["delta_V", "VN", "delta_U", "delta_L"][index];
          return {
            name: seriesName,
            data: s.data,
            type: "line",
            symbol: "none",
            color: seriesStyles[seriesName]?.color,
            lineStyle: seriesStyles[seriesName]?.lineStyle,
          };
        }),
      };
    },
  },
  watch: {
    "reportData.image_url": {
      handler(newUrl) {
        this.handleImageSourceChange("original", newUrl);
      },
      immediate: true,
    },
    "reportData.image_roi_url": {
      handler(newUrl) {
        this.handleImageSourceChange("roi", newUrl);
      },
      immediate: true,
    },
  },
  methods: {
    isTifUrl(url) {
      if (!url) return false;
      const lower = String(url).toLowerCase();
      return lower.endsWith(".tif") || lower.endsWith(".tiff");
    },
    getDisplayUrl(type, url) {
      if (!url) return "";
      const converted = this.convertedImageUrls[type];
      if (converted) {
        return converted;
      }
      if (!this.isTifUrl(url)) {
        return url;
      }
      if (!this.convertingMap[type]) {
        this.convertTifImage(type, url);
      }
      return "";
    },
    handleImageSourceChange(type, url) {
      this.$delete(this.conversionErrors, type);
      this.$set(this.convertedImageUrls, type, null);
      if (!url) {
        this.$set(this.convertingMap, type, false);
        return;
      }
      if (this.isTifUrl(url)) {
        this.convertTifImage(type, url);
      } else {
        this.$set(this.convertingMap, type, false);
      }
    },
    async convertTifImage(type, url) {
      if (!url || this.convertingMap[type]) {
        return;
      }
      this.$set(this.convertingMap, type, true);
      this.$delete(this.conversionErrors, type);
      try {
        const isMobile = window.innerWidth <= 768;
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), isMobile ? 30000 : 15000);
        
        const response = await fetch(url, {
          cache: "no-store",
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.statusText}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        const ifds = UTIF.decode(arrayBuffer);
        if (!ifds.length) {
          throw new Error("No image data found in TIF file");
        }
        const firstImage = ifds[0];
        UTIF.decodeImage(arrayBuffer, firstImage);
        let { width, height } = firstImage;
        if (!width || !height) {
          throw new Error("Invalid TIF image size");
        }
        
        const maxCanvasSize = isMobile ? 2048 : 4096;
        let scale = 1;
        if (width > maxCanvasSize || height > maxCanvasSize) {
          scale = Math.min(maxCanvasSize / width, maxCanvasSize / height);
          width = Math.floor(width * scale);
          height = Math.floor(height * scale);
        }
        
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        
        if (!ctx) {
          throw new Error("无法创建canvas上下文");
        }
        
        const imageData = ctx.createImageData(width, height);
        const rgba = UTIF.toRGBA8(firstImage);
        
        if (scale < 1) {
          const originalWidth = firstImage.width;
          const originalHeight = firstImage.height;
          const invScale = 1 / scale;
          
          for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
              const srcX = Math.min(Math.floor(x * invScale), originalWidth - 1);
              const srcY = Math.min(Math.floor(y * invScale), originalHeight - 1);
              const srcIdx = (srcY * originalWidth + srcX) * 4;
              const dstIdx = (y * width + x) * 4;
              imageData.data[dstIdx] = rgba[srcIdx];
              imageData.data[dstIdx + 1] = rgba[srcIdx + 1];
              imageData.data[dstIdx + 2] = rgba[srcIdx + 2];
              imageData.data[dstIdx + 3] = rgba[srcIdx + 3];
            }
          }
        } else {
          imageData.data.set(rgba);
        }
        
        ctx.putImageData(imageData, 0, 0);
        const dataUrl = canvas.toDataURL("image/png");
        
        canvas.width = 0;
        canvas.height = 0;
        
        // 使用$set确保响应式更新，并强制触发视图更新
        this.$set(this.convertedImageUrls, type, dataUrl);
        
        // 手机端：强制更新视图，确保图片显示
        const isMobileForUpdate = window.innerWidth <= 768;
        if (isMobileForUpdate) {
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
      } catch (error) {
        console.error(`Failed to convert TIF image (${type}):`, error);
        this.$set(
          this.conversionErrors,
          type,
          (error && error.message) || "convert failed"
        );
        this.$set(this.convertedImageUrls, type, null);
      } finally {
        this.$set(this.convertingMap, type, false);
      }
    },
    togglePrintingState(isPrinting) {
      this.isPrinting = isPrinting;
    },
    getChartInstances() {
      const instances = [];
      if (this.$refs.oecfCurveOption_Chart) {
        instances.push(this.$refs.oecfCurveOption_Chart.chart);
      }
      if (this.$refs.noiseValueOption_Chart) {
        instances.push(this.$refs.noiseValueOption_Chart.chart);
      }
      if (this.$refs.rmsNoiseSnrOption_Chart) {
        instances.push(this.$refs.rmsNoiseSnrOption_Chart.chart);
      }
      if (this.$refs.fStopNoiseSnrOption_Chart) {
        instances.push(this.$refs.fStopNoiseSnrOption_Chart.chart);
      }
      if (this.$refs.snrIsoOption_Chart) {
        instances.push(this.$refs.snrIsoOption_Chart.chart);
      }
      if (this.$refs.visualNoise1Option_Chart) {
        instances.push(this.$refs.visualNoise1Option_Chart.chart);
      }
      if (this.$refs.visualNoise2Option_Chart) {
        instances.push(this.$refs.visualNoise2Option_Chart.chart);
      }
      if (this.$refs.visualSnr1Option_Chart) {
        instances.push(this.$refs.visualSnr1Option_Chart.chart);
      }
      if (this.$refs.visualSnr2Option_Chart) {
        instances.push(this.$refs.visualSnr2Option_Chart.chart);
      }
      return instances;
    },
    ensureChartsRendered() {
      const chartInstances = this.getChartInstances();
      if (!chartInstances || chartInstances.length === 0) {
        return Promise.resolve();
      }
      const promises = chartInstances.map(
        (chart) =>
          new Promise((resolve) => {
            const listener = () => {
              resolve();
              chart.off("finished", listener);
            };
            chart.on("finished", listener);
            setTimeout(() => {
              resolve();
              chart.off("finished", listener);
            }, 2000);
          })
      );
      return Promise.all(promises);
    },
    scrollToSection(sectionId) {
      if (sectionId === "top") {
        const scrollContainer = this.$el.closest(".el-scrollbar__wrap");
        if (scrollContainer) {
          scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
        }
        return;
      }
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
  },
};
</script>

<style scoped>
.custom-table ::v-deep .el-table__cell {
  padding: 8px 0;
}
.labeled-image-container {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.image-label-wrapper {
  padding: 0 5px;
  text-align: left;
}
.dynamic-range-report-detail-container {
  padding-right: 15px;
}
.report-header-images {
  display: flex;
  gap: 20px;
  margin-bottom: 25px;
  justify-content: center;
}
.image-wrapper {
  border: 1px solid #e6ebf5;
  border-radius: 6px;
  padding: 20px;
  background-color: #fcfcfc;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 160px;
}
.image-wrapper img {
  width: 100%;
  height: auto;
  border-radius: 4px;
  display: block;
}
.image-loading,
.image-error {
  width: 100%;
  text-align: center;
  color: #909399;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.image-loading .el-icon-loading {
  font-size: 24px;
  color: #409eff;
}
.image-error {
  color: #f56c6c;
}
.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin-top: 35px;
  margin-bottom: 20px;
  scroll-margin-top: 15px;
}
.sub-section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-top: 25px;
  margin-bottom: 15px;
  padding-left: 12px;
  border-left: 4px solid #00a0e9;
}
.table-title {
  font-size: 14px;
  font-weight: 600;
  color: #606266;
  margin-top: 20px;
  margin-bottom: 10px;
}
.chart-container {
  width: 100%;
  margin-bottom: 20px;
  padding: 5px;
  border: 1px solid #e6ebf5;
  border-radius: 6px;
  box-sizing: border-box;
}
.echart {
  height: 500px;
  width: 100%;
}
.chart-inner-wrapper {
  max-width: 100%;
  margin: 0 auto;
}
.custom-table {
  margin-bottom: 20px;
}
.custom-table ::v-deep th {
  background-color: #f5f7fa !important;
  color: #606266;
  text-align: center;
}
.custom-table ::v-deep th .cell {
  font-weight: 700;
}
.custom-table ::v-deep td {
  text-align: center;
}
.custom-table ::v-deep(tr:not(.colored-row) > td) {
  background-color: #fff;
}
.custom-table ::v-deep(.colored-row > td) {
  background-color: #faf9fe !important;
}
.side-by-side-tables {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}
.el-table--enable-row-hover .el-table__body tr:hover > td {
  -webkit-box-shadow: none;
  box-shadow: none;
}
.result-summary-box {
  background-color: #f7f8fa;
  border-radius: 6px;
  padding: 12px 20px;
  margin-top: -10px;
  margin-bottom: 20px;
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}
.result-summary-box strong {
  color: #303133;
  font-weight: 600;
  margin-right: 8px;
}
@media (max-width: 768px) {
  .report-header-images,
  .analysis-tables-container {
    flex-direction: column;
  }
  .image-wrapper {
    padding: 15px;
  }
  .labeled-image-container {
    max-width: none;
  }
}
</style>
